import { useState, useRef } from "react";
import AppPage from "../AppPage";
import { globals, images, utilities } from "../../styles";
import {
  arrow_Icon,
  chevron_Icon,
  circle_Icon,
  star_Icon,
  crown_Icon,
  artist_Icon,
  music_Icon,
  vinyl_Icon,
} from "../../assets/icons";

const pages = [
  {
    image: music_Icon,
    subheading: "Intro",
    paragraph:
      "Band Draft is a season long Fantasy Music League. The season starts in the summer and runs to the Grammy's in January. You spend your starting money to recruit 8 artists or bands into your line-up. Artists are priced according to popularity so superstars cost a lot more than one-hit wonders and indie gems. Artists and bands earn points every week based on their Streams, Social Media Impact, and Live Shows. Artists also earn bonus points for Grammy nominations (200 points per nomination) and awards (500 points per Grammy) at the end of the season.",
  },
  {
    image: vinyl_Icon,
    subheading: "Scoring",
    paragraph:
      "In order to make the Band Draft fair to Artists of all sizes, scoring points gets harder the bigger an Artist gets. For example: If Britney Spears streams 20,000,000 million songs she gets 11 points for that day. The next day she streams 1 million more songs she gets 11.3 so plus 0.3 points. But if the Indigo Girls stream 1,000,000 songs one day they get 3 points, and if they then stream 2,000,000 the next day they get 4 points. This works the same for social media impact and live shows. Plus, Headliners at live shows will earn full points for a show, while the openers will receive a portion of the full points instead.",
  },
  {
    image: artist_Icon,
    subheading: "Line-up",
    paragraph:
      "The costs of artists and bands goes up or down every week based on how well the band scored the week before. Try to get bands early before their price goes up. But be warned, prices can rise (and fall) quickly. Every week you are allowed to change two of your artists for free, but every artist you drop after that will only refund half of their current price. Unused full refunds carry over to the next week giving you, to a max of 5 free trades at any given time.",
  },
  {
    image: star_Icon,
    subheading: "Boosting",
    paragraph:
      "Boost Button - every week you can choose a band to Boost or not. Boosted bands earn double points for that week. This has no effect on the boosted artist's cost.",
  },
  {
    image: crown_Icon,
    subheading: "Global Competition",
    paragraph:
      "All players are automatically eligible for the global competition. At the end of the season the player that has earned the most points over the year wins First Prize! Players can make public or private groups, so you can compete with your friends or anyone in the community. Plus, you can make 5 lineups - that's 5 different shots at winning first place!",
  },
];

function HowToPlayPage(props: { handleBackButton: () => void }) {
  const { handleBackButton } = props;
  const [currentPage, setCurrentPage] = useState(0);
  const touchStartX = useRef<number>(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 50) {
      setCurrentPage((p) =>
        delta > 0 ? Math.min(p + 1, pages.length - 1) : Math.max(p - 1, 0),
      );
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    touchStartX.current = e.clientX;
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    const delta = touchStartX.current - e.clientX;
    if (Math.abs(delta) > 50) {
      setCurrentPage((p) =>
        delta > 0 ? Math.min(p + 1, pages.length - 1) : Math.max(p - 1, 0),
      );
    }
  };

  return (
    <AppPage className={globals["page"]}>
      <div
        className={`${utilities["flex-center"]} ${utilities["gap-20"]} ${utilities["margin-10-0"]}`}
      ></div>
      <button
        className={`${globals["button-wrapper"]} ${globals["back-btn--top-left"]}`}
        onClick={handleBackButton}
      >
        <div className={globals["back-btn"]}>
          <img src={arrow_Icon} alt="" />
        </div>
      </button>
      <span className={globals["how-to-play-header"]}>How to Play</span>
      <div
        className={globals["carousel-wrapper"]}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      >
        <div
          className={globals["carousel-track"]}
          style={{ transform: `translateX(-${currentPage * 100}%)` }}
        >
          {pages.map((page, index) => (
            <div key={index} className={globals["carousel-page"]}>
              <span className={globals["how-to-play-subheading"]}>
                {page.subheading}
              </span>
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <button
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 0))}
                  disabled={currentPage === 0}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: currentPage === 0 ? "default" : "pointer",
                    opacity: currentPage === 0 ? 0.2 : 1,
                  }}
                >
                  <img
                    src={chevron_Icon}
                    alt="Previous"
                    style={{ width: 80, height: 80, transform: "scaleX(-1)" }}
                  />
                </button>
                <img
                  src={page.image}
                  className={images["how-to-play"]}
                  alt="How to play"
                />
                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(p + 1, pages.length - 1))
                  }
                  disabled={currentPage === pages.length - 1}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor:
                      currentPage === pages.length - 1 ? "default" : "pointer",
                    opacity: currentPage === pages.length - 1 ? 0.2 : 1,
                  }}
                >
                  <img
                    src={chevron_Icon}
                    alt="Next"
                    style={{ width: 80, height: 80 }}
                  />
                </button>
              </div>
              <p className={globals["how-to-play-paragraph"]}>
                {page.paragraph}
              </p>
            </div>
          ))}
        </div>
      </div>
      <div className={globals["carousel-page-indicator-container"]}>
        {Array.from({ length: pages.length }, (_, i) => (
          <img
            key={i}
            src={circle_Icon}
            alt=""
            className={
              i === currentPage
                ? images["carousel-circle-icon"]
                : images["carousel-circle-icon--inactive"]
            }
          />
        ))}
      </div>
    </AppPage>
  );
}

export default HowToPlayPage;
